import React, { useState, useEffect, useMemo } from 'react';
import { Prompt } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Table } from 'reactstrap';
import Pagination from 'components/molecules/pagination';
import CommentForm from 'components/molecules/commentForm';
import { openInNewTab, showResult } from 'utils';
import withLoaderTable from 'components/organisms/withLoader/withLoaderTable';
import MessageIcon from 'components/atoms/icons/messageIcon';
import { isCcmActionAllowedStatus } from 'utils/userTypeHelper';
import CommentItem from './commentItem';
import PcctModal from './pcctModal';

const GeneralComments = ({
  patientGuid,
  isAdminPanel,
  isCcmPanel,
  isRpmPanel,
  profile,
}) => {
  const loading = useSelector(
    (state) => state.loading.effects.comments.getComments,
  );
  const comments = useSelector((state) => state.comments);
  const isAudioLoading = useSelector(
    (state) => state.loading.effects.comments.downloadCommentAudio,
  );
  const {
    comments: {
      getComments,
      setPage: setCommentsPage,
      submit,
      downloadCommentAudio,
    },
    doctor: { addGeneralComment, removeGeneralComment, clearGeneralComment },
  } = useDispatch();
  const commentSubmitLoading = useSelector(
    (state) => state.loading.effects.comments.submit,
  );
  const [comment, setComment] = useState('');
  const [commentType, setCommentType] = useState('rpm');
  const [pcctValues, setPcctValues] = useState({});
  const [pcctPopupOpen, setPcctPopupOpen] = useState(false);
  useMemo(() => {
    if (isRpmPanel) return setCommentType('rpm');
    if (isCcmPanel) return setCommentType('ccm');
  }, [isCcmPanel, isRpmPanel]);
  const handleSubmit = async (values, { resetForm }) => {
    const postData = {
      patientGuid,
      type: 'text',
      message: values.comment,
      commentType,
    };
    const { error } = await submit(postData);
    showResult(error);
    if (error === undefined) {
      resetForm({});
    }
    setPcctValues({});
    setComment('');
  };

  const setPage = (pageOffset) => {
    const { page } = comments;
    setCommentsPage(page + pageOffset);
    getComments(patientGuid);
  };

  const downloadAudio = async (callId) => {
    const { error, data } = await downloadCommentAudio({ patientGuid, callId });
    if (error) {
      return showResult(error);
    }
    if (data && data.url) return openInNewTab(data.url);
  };

  const commentChanged = (value) => {
    if (value) addGeneralComment(value);
    else removeGeneralComment(value);
    setComment(value);
  };

  const handleCommentType = () => {
    if (commentType === 'rpm') setCommentType('ccm');
    else setCommentType('rpm');
  };

  const canEditPage = isCcmActionAllowedStatus(profile?.ccmStatus);
  let showInput = true;
  if (isCcmPanel && !canEditPage) {
    showInput = false;
  }

  useEffect(() => {
    setPcctValues({});
    setPcctPopupOpen(false);
    setComment('');
    getComments(patientGuid);
  }, [patientGuid]);

  useEffect(() => {
    clearGeneralComment();
  }, []);

  return (
    <div className="general-comments">
      <Prompt
        when={!!Object.keys(pcctValues).length}
        message="You have unsaved changes, are you sure you want to leave?"
      />
      <Table
        className="general-comments-table"
        borderless
        border="0"
        cellSpacing="0"
        cellPadding="0"
      >
        <thead>
          <tr>
            <th colSpan="3">
              <Pagination
                title="General Comments"
                page={comments.page}
                total={comments.totalCount}
                itemsPerPage={comments.pageStep}
                pageTotal={comments.data.length}
                handlePrevClick={() => setPage(-1)}
                handleNextClick={() => setPage(+1)}
                handleFirstClick={() => setPage(-(comments.page - 1))}
                handleLastClick={() =>
                  setPage(
                    Math.ceil(comments.totalCount / comments.pageStep) -
                      comments.page,
                  )
                }
                loading={loading}
              />
            </th>
          </tr>
        </thead>

        {withLoaderTable(
          loading,
          <tbody className={isAdminPanel ? 'gray-scrollbar' : null}>
            {comments.data.map((c, index) => (
              <CommentItem
                key={index + 1}
                id={index + 1}
                comment={c}
                downloadAudio={downloadAudio}
                loading={isAudioLoading}
                solid={isAdminPanel}
              />
            ))}
          </tbody>,
        )}

        {showInput && (
          <tfoot className={isAdminPanel && 'admin-comment-footer'}>
            <tr>
              <td colSpan="3" style={{ position: 'relative' }}>
                <CommentForm
                  valueChanged={commentChanged}
                  handleSubmit={handleSubmit}
                  disabled={commentSubmitLoading}
                  adminPage={isAdminPanel}
                  comment={comment}
                  commentType={commentType}
                  handleCommentType={handleCommentType}
                  hideCount
                />
                <span
                  className={
                    isAdminPanel ? 'admin-comment-icon' : 'comment-icon'
                  }
                  onClick={() => setPcctPopupOpen(!pcctPopupOpen)}
                >
                  <MessageIcon />
                </span>
              </td>
            </tr>
          </tfoot>
        )}
      </Table>
      {pcctPopupOpen && (
        <PcctModal
          isOpen={pcctPopupOpen}
          closeModal={() => setPcctPopupOpen(false)}
          values={pcctValues}
          setValues={setPcctValues}
          setComment={setComment}
        />
      )}
    </div>
  );
};

GeneralComments.propTypes = {
  patientGuid: PropTypes.string.isRequired,
  isAdminPanel: PropTypes.bool,
  isCcmPanel: PropTypes.bool,
  isRpmPanel: PropTypes.bool,
  profile: PropTypes.object,
};

export default GeneralComments;
